import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { FineDistributionPanel } from '@/pages/anatel/FineDistributionPanel';
import { cadeLedgerParams, fetchCadeFineLedger, fetchCadeSanctions } from '@/services/cade';
import { Stat } from '@/pages/cade/Stat';
import { WindowCaption } from '@/pages/cade/WindowCaption';
import type {
  CadeAttributionProvenance,
  CadeCohortComparison,
  CadeFineCohort,
  CadeFineLedgerRow,
  CadeLedgerQuery,
  CadeLedgerReach,
} from '@/services/types';

const LEDGER_PAGE_SIZE = 50;

/**
 * CADE fines, and the two things that make them easy to misreport.
 *
 * CURRENCY. 48 of 590 fines carry an amount only in UFIR, an inflation index.
 * The distribution is BRL-only and says so: there is no conversion rate in this
 * corpus, and coercing one would put an invented number inside a percentile.
 *
 * PROVENANCE. Every attribution reports a verified evidence span, which is one
 * number covering two different guarantees. A language model quoted 253 of them
 * and the check is real; a regular expression cut 182 out of the source text, so
 * those verify by construction. The panel splits them rather than printing
 * "435 of 435" and letting a tautology read as assurance.
 *
 * There is no per-company total anywhere here. Party names in this corpus resolve
 * to nothing, so a league table would rank spellings rather than firms.
 *
 * Below the panels sits the LEDGER: every amount the Tribunal minuted, one row
 * per fine row, largest first, with the case, the session, the relator, the
 * outcome and the party where the amount is attributed to exactly one. It
 * replaced a top-25 list of attributed fines on 2026-09-14 -- a practitioner
 * asks what comparable matters cost, and a top-25 answers a different question.
 */
export function CadeSanctionsPage() {
  const { t, i18n } = useTranslation(['cade', 'common']);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'sanctions'],
    queryFn: fetchCadeSanctions,
  });

  const number = new Intl.NumberFormat(i18n.language);

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:sanctions.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:sanctions.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cade:sanctions.explainer" />

      <AsOfStrip asOf={data.as_of} />

      {/* The currency exclusion leads, because a "total fines" reader will
          otherwise assume the distribution covers every fine. */}
      {data.ufir_only > 0 ? (
        <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden
          />
          {t('cade:sanctions.ufirExcluded', {
            ufir: number.format(data.ufir_only),
            fines: number.format(data.fines),
          })}
        </p>
      ) : null}

      <FineDistributionPanel
        answer={data.distribution}
        titleKey="cade:sanctions.distribution.title"
        helpKey="cade:sanctions.distribution.help"
      />

      <FieldGuide i18nKey="cade:sanctions.guide.distribution" />

      <CohortComparison comparison={data.cohort_comparison} />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:sanctions.provenance.title')}
        </h2>
        <Answer answer={data.provenance} help={t('cade:sanctions.provenance.help')}>
          {(provenance) => <ProvenancePanel provenance={provenance} />}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:sanctions.guide.provenance" />
        </div>
      </section>

      <LedgerSection />
    </div>
  );
}

/**
 * TCC homologations against condemnations, as fine cohorts.
 *
 * This is what a negotiation posture is built from -- what the homologated
 * agreements produced and what the condemnations produced -- and it is
 * deliberately NOT differenced into "how much settling saves". A TCC's amounts
 * are negotiated pecuniary contributions and a condemnation's are imposed
 * sanctions: different instruments from different processes, so the two
 * ladders stand side by side and the intro says why no ratio is drawn.
 *
 * Each cohort's distribution is its own Answer, so one can refuse while the
 * other shows -- the measured reality today, where the TCC cohort's rows fall
 * below the distribution floor.
 */
function CohortComparison({ comparison }: { comparison: CadeCohortComparison }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">
          {t('cade:sanctions.cohorts.title')}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('cade:sanctions.cohorts.intro', { both: number.format(comparison.both_codes) })}
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <CohortCard cohort={comparison.tcc} nameKey="cade:sanctions.cohorts.tcc" />
        <CohortCard cohort={comparison.condemned} nameKey="cade:sanctions.cohorts.condemned" />
      </div>
    </section>
  );
}

function CohortCard({ cohort, nameKey }: { cohort: CadeFineCohort; nameKey: string }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="rounded border p-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-xs font-semibold text-foreground">{t(nameKey)}</h3>
        <p className="text-xs tabular-nums text-muted-foreground">
          {t('cade:sanctions.cohorts.coverage', {
            decisions: number.format(cohort.decisions),
            withFines: number.format(cohort.decisions_with_fines),
            rows: number.format(cohort.fine_rows),
          })}
        </p>
      </div>
      <FineDistributionPanel
        answer={cohort.distribution}
        titleKey="cade:sanctions.cohorts.distribution"
        helpKey="cade:sanctions.cohorts.help"
      />
    </div>
  );
}

function ProvenancePanel({ provenance }: { provenance: CadeAttributionProvenance }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-3 text-xs">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={t('cade:sanctions.provenance.byModel')}
          value={number.format(provenance.by_model)}
        />
        <Stat
          label={t('cade:sanctions.provenance.byPattern')}
          value={number.format(provenance.by_pattern)}
        />
        <Stat
          label={t('cade:sanctions.provenance.parties')}
          value={number.format(provenance.parties)}
        />
        <Stat
          label={t('cade:sanctions.provenance.decisions')}
          value={`${number.format(provenance.decisions)} / ${number.format(
            provenance.decisions_with_fines,
          )}`}
        />
      </dl>

      {/* The per-extractor list (model · prompt · rows) was removed by request
          on 2026-09-14. The model-versus-pattern split above is what a reader
          needs to weigh a verified span; the names travel in the export and on
          every row of the list below. */}
      {provenance.by_pattern > 0 ? (
        <p className="rounded border border-amber-500/40 bg-amber-500/5 p-2 text-foreground">
          {t('cade:sanctions.provenance.patternCaveat', {
            pattern: number.format(provenance.by_pattern),
            total: number.format(provenance.attributions),
          })}
        </p>
      ) : null}

      {provenance.duplicated > 0 ? (
        <p className="rounded border border-dashed bg-muted/30 p-2 text-muted-foreground">
          {t('cade:sanctions.provenance.duplicated', {
            rows: number.format(provenance.duplicated),
          })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The fine ledger: every minuted amount, as recorded, largest first.
 *
 * Its own query, its own filters, its own export. The filters ARE the query
 * key, so changing one drops every accumulated batch and starts again; batches
 * append as the reader scrolls (`useAutoLoadMore`), and the "load more"
 * button is driven by the server's total, never by "the last batch was full".
 *
 * Three things the reach panel says before the rows, because each is a number
 * a reader would otherwise get wrong in a filing: an amount minuted on an
 * adjourned item is the relator's proposal, not a fine imposed (flagged per
 * row); a row on a decision that is not the case's last observed one may have
 * been superseded (a filter); and a party is named only where exactly one
 * attribution on the decision carries this exact amount -- several parties
 * minuted the same amount cannot be told apart, so the row counts them and the
 * decision page lists them. Nothing here is totalled per party.
 */
function LedgerSection() {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const [outcome, setOutcome] = useState('');
  const [year, setYear] = useState('');
  const [caseType, setCaseType] = useState('');
  const [minBrl, setMinBrl] = useState('');
  const [onlyFinal, setOnlyFinal] = useState(false);
  const [onlyAttributed, setOnlyAttributed] = useState(false);

  const filters: CadeLedgerQuery = {
    outcome: outcome || null,
    sessionYear: year ? Number(year) : null,
    caseType: caseType || null,
    minBrl: minBrl ? Number(minBrl) : null,
    onlyFinal,
    onlyAttributed,
  };

  const { data, isPending, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['cade', 'sanctions', 'ledger', filters],
      queryFn: ({ pageParam }) =>
        fetchCadeFineLedger({ ...filters, limit: LEDGER_PAGE_SIZE, offset: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
        return loaded < lastPage.total ? loaded : undefined;
      },
    });
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const first = data?.pages[0];
  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const number = new Intl.NumberFormat(i18n.language);
  const exportParams = cadeLedgerParams(filters);

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">{t('cade:sanctions.ledger.title')}</h2>
        <p className="mt-1 text-xs text-muted-foreground">{t('cade:sanctions.ledger.intro')}</p>
      </div>

      {first ? (
        <>
          <Answer answer={first.reach} help={t('cade:sanctions.ledger.help')}>
            {(reach: CadeLedgerReach) => <LedgerReachPanel reach={reach} />}
          </Answer>
          <WindowCaption window={first.as_of.year_window} />
        </>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <label className="block min-w-[12rem] space-y-1">
          <span className="text-xs font-medium text-foreground">
            {t('cade:sanctions.ledger.filters.outcome')}
          </span>
          <select
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="">{t('cade:sanctions.ledger.filters.outcomeAny')}</option>
            {(first?.options.outcome_codes ?? []).map((code) => (
              <option key={code} value={code}>
                {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
              </option>
            ))}
          </select>
        </label>
        <label className="block min-w-[14rem] space-y-1">
          <span className="text-xs font-medium text-foreground">
            {t('cade:sanctions.ledger.filters.caseType')}
          </span>
          <select
            value={caseType}
            onChange={(event) => setCaseType(event.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="">{t('cade:sanctions.ledger.filters.caseTypeAny')}</option>
            {(first?.options.case_types ?? []).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label className="block min-w-[8rem] space-y-1">
          <span className="text-xs font-medium text-foreground">
            {t('cade:sanctions.ledger.filters.year')}
          </span>
          <select
            value={year}
            onChange={(event) => setYear(event.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="">{t('cade:sanctions.ledger.filters.yearAny')}</option>
            {(first?.options.years ?? []).map((value) => (
              <option key={value} value={String(value)}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="block min-w-[10rem] space-y-1">
          <span className="text-xs font-medium text-foreground">
            {t('cade:sanctions.ledger.filters.minBrl')}
          </span>
          <input
            type="number"
            min={0}
            step={1000}
            value={minBrl}
            onChange={(event) => setMinBrl(event.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-xs text-foreground">
          <input
            type="checkbox"
            checked={onlyFinal}
            onChange={(event) => setOnlyFinal(event.target.checked)}
            className="h-4 w-4 rounded border"
          />
          {t('cade:sanctions.ledger.filters.onlyFinal')}
        </label>
        <label className="flex items-center gap-2 text-xs text-foreground">
          <input
            type="checkbox"
            checked={onlyAttributed}
            onChange={(event) => setOnlyAttributed(event.target.checked)}
            className="h-4 w-4 rounded border"
          />
          {t('cade:sanctions.ledger.filters.onlyAttributed')}
        </label>
      </div>

      <div className="flex justify-end">
        <ExportButton path={`/api/v1/cade/export/sanctions/ledger?${exportParams.toString()}`} />
      </div>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !first ? (
        <SkeletonLoader rows={8} />
      ) : items.length === 0 ? (
        <EmptyState hint={t('cade:sanctions.ledger.emptyHint')} />
      ) : (
        <>
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-loaded={items.length}
            data-total={first.total}
          >
            {t('cade:sanctions.ledger.showing', {
              loaded: number.format(items.length),
              total: number.format(first.total),
            })}
          </p>
          <div
            ref={scrollRef}
            className="max-h-[65vh] overflow-auto rounded-lg border"
            role="region"
            aria-label={t('cade:sanctions.ledger.tableLabel')}
          >
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('cade:sanctions.ledger.columns.case')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:sanctions.ledger.columns.year')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:sanctions.ledger.columns.type')}</th>
                  <th className="px-3 py-2 font-medium">
                    {t('cade:sanctions.ledger.columns.outcome')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('cade:sanctions.ledger.columns.relator')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('cade:sanctions.ledger.columns.party')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:sanctions.ledger.columns.amount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, index) => (
                  // Two rows can minute the same amount on the same decision
                  // and carry no natural tiebreak, so the key is the index;
                  // the list is server-ordered and never reordered here.
                  <LedgerRow key={`${row.decision_key}-${index}`} row={row} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <div className="flex justify-center py-3">
              {hasNextPage ? (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isFetchingNextPage}
                  className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                >
                  {isFetchingNextPage
                    ? t('cade:cases.loadingMore')
                    : t('cade:sanctions.ledger.loadMore', { count: LEDGER_PAGE_SIZE })}
                </button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('cade:sanctions.ledger.allLoaded', { total: number.format(first.total) })}
                </p>
              )}
            </div>
          </div>
        </>
      )}
      <FieldGuide i18nKey="cade:sanctions.guide.ledger" />
    </section>
  );
}

function LedgerReachPanel({ reach }: { reach: CadeLedgerReach }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <dl className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-5">
      <Stat label={t('cade:sanctions.ledger.reach.rows')} value={number.format(reach.rows)} />
      <Stat
        label={t('cade:sanctions.ledger.reach.priced')}
        value={number.format(reach.priced)}
        hint={t('cade:sanctions.ledger.reach.ufirOnly', { count: reach.ufir_only })}
      />
      <Stat
        label={t('cade:sanctions.ledger.reach.adjourned')}
        value={number.format(reach.adjourned)}
      />
      <Stat label={t('cade:sanctions.ledger.reach.onFinal')} value={number.format(reach.on_final)} />
      <Stat
        label={t('cade:sanctions.ledger.reach.attributed')}
        value={number.format(reach.attributed)}
        hint={t('cade:sanctions.ledger.reach.ambiguous', { count: reach.ambiguous })}
      />
    </dl>
  );
}

function LedgerRow({ row }: { row: CadeFineLedgerRow }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className="px-3 py-2">
        <Link
          to={`/cade/votes/d/${encodeURIComponent(row.decision_key)}`}
          className="tabular-nums text-primary underline-offset-4 hover:underline"
        >
          {row.case_number}
        </Link>
        <div className="text-xs text-muted-foreground">
          {row.item_number ? t('cade:case.item', { item: row.item_number }) : null}
          {row.session_number ? ` · ${t('cade:sessions.numbered', { number: row.session_number })}` : ''}
          {row.is_final_observed ? ` · ${t('cade:sanctions.ledger.final')}` : ''}
        </div>
      </td>
      <td className="px-3 py-2 tabular-nums text-foreground">{row.session_year ?? '—'}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{row.case_type ?? '—'}</td>
      <td className="px-3 py-2">
        {row.outcome_codes.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {row.outcome_codes.map((code) => (
              <span key={code} className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
        {/* Keyed on the backend's flag, not on a code re-listed here: the
            codes that mean "adjourned" are spelled once, in the analytics
            layer, and this hint follows them. */}
        {row.adjourned ? (
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
            {t('cade:sanctions.ledger.adjourned')}
          </p>
        ) : null}
      </td>
      <td className="px-3 py-2 text-xs">
        {row.relator && row.relator_slug ? (
          <Link
            to={`/cade/r/${row.relator_slug}`}
            className="text-primary underline-offset-4 hover:underline"
          >
            {row.relator}
          </Link>
        ) : (
          <span className="text-muted-foreground">{row.relator ?? '—'}</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs">
        <LedgerParty row={row} />
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-foreground">
        {row.amount_brl !== null ? (
          currency.format(row.amount_brl)
        ) : row.amount_ufir !== null ? (
          <span title={t('cade:sanctions.ledger.ufirTitle')}>
            {t('cade:sanctions.ledger.ufir', { amount: number.format(row.amount_ufir) })}
          </span>
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}

/**
 * The party cell, three ways: one attribution names the party (linked where a
 * party row exists, plain text where only a raw name does -- a dead link the
 * catch-all turns into a valid-looking page is worse than none); several
 * attributions share the amount and the cell counts them, pointing at the
 * decision page where they are listed; none, a dash.
 */
function LedgerParty({ row }: { row: CadeFineLedgerRow }) {
  const { t } = useTranslation(['cade']);
  if (row.attributed_parties === 0) return <span className="text-muted-foreground">—</span>;
  if (row.attributed_parties > 1) {
    return (
      <Link
        to={`/cade/votes/d/${encodeURIComponent(row.decision_key)}`}
        className="text-primary underline-offset-4 hover:underline"
      >
        {t('cade:sanctions.ledger.severalParties', { count: row.attributed_parties })}
      </Link>
    );
  }
  return (
    <>
      {row.party_slug ? (
        <Link
          to={`/cade/parties/${row.party_slug}`}
          className="text-primary underline-offset-4 hover:underline"
        >
          {row.party_name}
        </Link>
      ) : (
        <span className="text-foreground">{row.party_name}</span>
      )}
      {row.from_pattern ? (
        <span
          className="ml-1 rounded border border-amber-500/40 px-1 text-[10px] text-amber-700 dark:text-amber-400"
          title={t('cade:sanctions.ledger.patternTitle')}
        >
          {t('cade:sanctions.ledger.pattern')}
        </span>
      ) : null}
    </>
  );
}
